import { DeleteOutlined, EditOutlined, RollbackOutlined, SendOutlined } from '@ant-design/icons';
import { Button, Drawer, PageHeader, Space } from 'antd';
import { Profile } from 'oidc-client';
import React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { DRAWER_WIDTH } from '../../../config/constants';
import ReplaceStrings from '../../../config/replaceStrings';
import Routes from '../../../config/routes';
import { translations } from '../../../config/translations';
import { ActionType, DrawerState, ModuleName } from '../../../core/models/enum';
import { RouteParamsGeneric } from '../../../core/models/RouteParams';
import { authorizeAction } from '../../../helpers/CheckPermissionHelper';
import { showConfirm, showError, showSuccess } from '../../../helpers/NotificationHelper';
import {
    ApiException,
    RoleEnum,
    ToolTransferDetailVm,
    ToolTransfersClient,
    TransferStatusEnum,
} from '../../../utils/api';
import TransferForm from '../transfer-form/TransferForm';
import {
    getSiteManagerProjects,
    isDestinationSide,
    isSourceSide,
    notifyTransfersChanged,
} from '../helpers';
import TransferStatusTag from '../transfer-status-tag/TransferStatusTag';
import TransferDetails from './transfer-details/TransferDetails';

interface Props extends RouteComponentProps<RouteParamsGeneric> {
    userProfile: Profile;
}

interface State {
    transfer?: ToolTransferDetailVm;
    drawerState: DrawerState;
}

class TransferPage extends React.Component<Props, State> {
    public constructor(props: Props) {
        super(props);

        this.state = {
            drawerState: DrawerState.Closed,
        };
    }

    public componentDidMount = () => {
        void this.getTransfer();
    };

    // The route component is reused when only the id changes (e.g. a link to another transfer).
    public componentDidUpdate = (prevProps: Props) => {
        if (prevProps.match.params.entityId !== this.props.match.params.entityId) {
            this.setState({ transfer: undefined, drawerState: DrawerState.Closed });
            void this.getTransfer();
        }
    };

    private getTransfer = async (): Promise<void> => {
        const {
            match: {
                params: { entityId },
            },
        } = this.props;

        if (!entityId) return;

        const transfer = await new ToolTransfersClient().getById(+entityId);
        this.setState({ transfer });
    };

    /**
     * SuperAdmin, Administrator and Skladiste act on any transfer; a Voditelj only on transfers touching a
     * warehouse he runs, and then only on the matching side. The server decides for real.
     */
    private permissions = () => {
        const { userProfile } = this.props;
        const { transfer } = this.state;

        const userRole = parseInt(userProfile['userRoleId']) as RoleEnum;
        const siteManagerProjects = getSiteManagerProjects(userProfile);
        const canWrite = authorizeAction(userProfile, ModuleName.Transfers, ActionType.Update);
        const isUnrestricted =
            userRole === RoleEnum.SuperAdmin ||
            userRole === RoleEnum.Admin ||
            userRole === RoleEnum.Warehouse;

        if (!transfer || !canWrite) {
            return { source: false, destination: false, editShipped: false };
        }

        return {
            source: isSourceSide(transfer, isUnrestricted, siteManagerProjects),
            destination: isDestinationSide(transfer, isUnrestricted, siteManagerProjects),
            // Skladište and Administrator (and SuperAdmin) may still correct a shipped transfer; others only drafts.
            editShipped: isUnrestricted,
        };
    };

    private runAction = async (
        action: (client: ToolTransfersClient, id: number) => Promise<ToolTransferDetailVm>,
        successMessage: string
    ) => {
        const { transfer } = this.state;

        if (!transfer) return;

        try {
            const updated = await action(new ToolTransfersClient(), transfer.id);
            this.setState({ transfer: updated });
            showSuccess(successMessage);
            notifyTransfersChanged();
        } catch (error) {
            if (error instanceof ApiException) {
                showError(error.response);
            } else {
                showError(translations.general.errorSavingData);
            }
        }
    };

    private handleShip = () =>
        showConfirm(
            () =>
                void this.runAction(
                    (client, id) => client.ship(id),
                    translations.transfers.shipSuccess
                ),
            translations.transfers.shipConfirm
        );

    private handleReturnToDraft = () =>
        showConfirm(
            () =>
                void this.runAction(
                    (client, id) => client.returnToDraft(id),
                    translations.transfers.returnToDraftSuccess
                ),
            translations.transfers.returnToDraftConfirm
        );

    private handleDelete = () => {
        const { history } = this.props;
        const { transfer } = this.state;

        if (!transfer) return;

        showConfirm(
            async () => {
                try {
                    await new ToolTransfersClient().delete(transfer.id);
                    showSuccess(translations.transfers.deleteSuccess);
                    history.push(Routes.ROUTE_TRANSFERS);
                } catch (error) {
                    if (error instanceof ApiException) {
                        showError(error.response);
                    } else {
                        showError(translations.general.errorDeletingData);
                    }
                }
            },
            translations.transfers.deleteConfirm,
            undefined,
            true
        );
    };

    private handleReceive = () => {
        const { history } = this.props;
        const { transfer } = this.state;

        if (!transfer) return;

        history.push(
            Routes.ROUTE_TRANSFERS_RECEIVE.replace(
                ReplaceStrings.ENTITY_ID,
                transfer.id.toString()
            )
        );
    };

    private handleEditSuccess = (transfer: ToolTransferDetailVm) => {
        this.setState({ transfer, drawerState: DrawerState.Closed });
    };

    private renderActions = (): React.ReactElement[] => {
        const { transfer } = this.state;

        if (!transfer) return [];

        const { source, destination, editShipped } = this.permissions();
        const actions: React.ReactElement[] = [];

        if (transfer.status === TransferStatusEnum.Draft && source) {
            actions.push(
                <Button
                    key="edit"
                    onClick={() => this.setState({ drawerState: DrawerState.Edit })}
                >
                    <EditOutlined />
                    {translations.general.edit}
                </Button>,
                <Button key="ship" type="primary" onClick={this.handleShip}>
                    <SendOutlined />
                    {translations.transfers.ship}
                </Button>,
                <Button key="delete" danger onClick={this.handleDelete}>
                    <DeleteOutlined />
                    {translations.general.delete}
                </Button>
            );
        }

        if (transfer.status === TransferStatusEnum.Shipped) {
            if (editShipped) {
                actions.push(
                    <Button
                        key="edit"
                        onClick={() => this.setState({ drawerState: DrawerState.Edit })}
                    >
                        <EditOutlined />
                        {translations.general.edit}
                    </Button>
                );
            }

            if (destination) {
                actions.push(
                    <Button key="receive" type="primary" onClick={this.handleReceive}>
                        {translations.transfers.receive}
                    </Button>
                );
            }

            if (source) {
                actions.push(
                    <Button key="return" onClick={this.handleReturnToDraft}>
                        <RollbackOutlined />
                        {translations.transfers.returnToDraft}
                    </Button>
                );
            }
        }

        return actions;
    };

    public render(): React.ReactElement {
        const { transfer, drawerState } = this.state;
        const { history } = this.props;

        if (!transfer) {
            return <PageHeader title={translations.transfers.transfer} />;
        }

        return (
            <>
                <PageHeader
                    title={transfer.fromName + ' → ' + transfer.toName}
                    tags={<TransferStatusTag status={transfer.status} />}
                    onBack={() => history.push(Routes.ROUTE_TRANSFERS)}
                    extra={<Space className="no-print">{this.renderActions()}</Space>}
                />

                <TransferDetails transfer={transfer} />

                <Drawer
                    title={translations.transfers.editTransfer}
                    open={drawerState === DrawerState.Edit}
                    onClose={() => this.setState({ drawerState: DrawerState.Closed })}
                    width={DRAWER_WIDTH}
                    destroyOnClose
                >
                    <TransferForm
                        transfer={transfer}
                        onClose={() => this.setState({ drawerState: DrawerState.Closed })}
                        onSuccess={this.handleEditSuccess}
                    />
                </Drawer>
            </>
        );
    }
}

export default withRouter(TransferPage);
